export default class UI_GoalPanelCollect extends Phaser.Group {
  /**
   * The panel for the goal screen when collecting a number of gems
   * @param {number} x
   * @param {number} y
   */
  constructor(x, y) {
    super(game);

    this.x = G.l(x);
    this.y = G.l(y);

    this._objectsToCollect = G.Utils.clone(G.lvlData.goal[1]);
    this.panels = []; // Accessed externally

    this._makePanels(this._objectsToCollect);

    this._signalBindings = [];
    this._signalBindings.push(G.sb('onTaskAmountChanged').add(this._updateDisplay, this));

    this._signalBindings.push(G.sb('onScreenResize').add(this._onScreenResize, this));
    this._onScreenResize();
  }

  /**
   * Destroy!
   */
  destroy() {
    if (this._signalBindings) {
      this._signalBindings.forEach((binding) => {
        if (binding.detach) {
          binding.detach();
        }
      });
      this._signalBindings = null;
    }
    this.panels.forEach((panel) => {
      panel.destroy();
    });
    super.destroy();
  }

  /**
   * On resize
   */
  _onScreenResize() {
    const horizontal = false;
    if (horizontal) {
      this._refreshPanelsHorizontalPositions();
    } else {
      let width = G.l(260);

      if (this.panels.length === 2) {
        width = G.l(130);
      } else if (this.panels.length === 3) {
        width = G.l(210);
      }

      let distance = 0;
      if (this.panels.length - 1) {
        distance = width / (this.panels.length - 1);
      }
      const startX = width * Math.sign(distance) * -0.5;

      this.panels.forEach((child, index) => {
        child.x = startX + (index * distance);
        child.y = 0;
        child.scale.setTo(0.5);
      });
    }

    this.panels.forEach((panel) => {
      if (horizontal) {
        panel.turnHorizontal();
      } else {
        panel.turnVertical();
      }
    });
  }

  /**
   * Gets a segment of the goal panel
   * @param {string} goalName
   */
  getGoalPanel(goalName) {
    for (let i = 0, len = this.panels.length; i < len; i++) {
      if (this.panels[i].goalName === goalName) {
        return this.panels[i];
      }
    }
    return null;
  }

  /**
   * Updates the goal number
   * @param {string} type
   * @param {number} change
   */
  _updateDisplay(type, change) {
    const panel = this.getGoalPanel(type);
    if (!panel.nr.alive) return;
    if (panel.nr.alive) {
      const newValue = parseInt(panel.nr.text) + change;
      panel.nr.setText(newValue);
      if (newValue === 0 && panel.nr.alive) {
        panel.checkmark.visible = true;
        panel.nr.destroy();
      }
    }
  }

  /**
   * Makes panel for goals
   * @param {number} x
   * @param {number} y
   * @param {string} name
   * @param {number} number
   * @param {number} scale
   */
  _makePanel(x, y, name, number, scale) {
    const gfxName = G.json['configs/gameplayGoals'][name].sprite;

    const panel = game.make.group();
    panel.x = G.l(x);
    panel.y = G.l(y);

    panel.scale.setTo(scale);
    panel.goalName = name;
    panel.amount = number;

    panel.nr = panel.add(new G.Text(38, 8, number.toString(), G.OMTsettings.elements.UI_GoalPanelCollect.panelNr.style, 0.5, 85));
    panel.img = G.makeImage(-40, 0, gfxName, 0.5, panel);
    panel.imgFade = G.makeImage(-40, 0, gfxName, 0.5, this);
    panel.imgFade.alpha = 0;

    panel.checkmark = G.makeImage(panel.nr.x, panel.nr.y, 'task_complete', [1, 0.5], panel);
    panel.checkmark.position = panel.nr.position;
    panel.checkmark.anchor = panel.nr.anchor;
    panel.checkmark.visible = false;

    panel.turnHorizontal = () => {
      panel.img.x = 0;
      panel.nr.x = 0;
      panel.nr.y = G.l(60);
      panel.nr.anchor.setTo(0.5);
      panel.nr.cacheAsBitmap = false;
    };

    panel.turnVertical = () => {
      panel.img.x = G.l(-40);
      panel.nr.x = G.l(38);
      panel.nr.y = 8;
      panel.nr.anchor.setTo(0.5);
      panel.nr.cacheAsBitmap = false;
    };

    panel.fadeAnim = () => {
      G.stopTweens(panel.imgFade);
      panel.imgFade.scale.setTo(0);
      panel.imgFade.alpha = 1;
      game.add.tween(panel.imgFade).to({ alpha: 0 }, 300, Phaser.Easing.Sinusoidal.Out, true);
      game.add.tween(panel.imgFade.scale).to({ x: 2, y: 2 }, 300, Phaser.Easing.Sinusoidal.Out, true);
    };

    this.add(panel);
    this.panels.push(panel);
  }

  /**
   * Makes the panels based on number of goals
   * Highly inefficient
   * @param {Array<any>} objects
   */
  _makePanels(objects) {
    if (objects.length === 1) {
      this._makePanel(0, -25, objects[0][0], objects[0][1], 0.8);
    }
    if (objects.length === 2) {
      this._makePanel(-30, 0, objects[0][0], objects[0][1], 0.6);
      this._makePanel(30, 0, objects[1][0], objects[1][1], 0.6);
    }
    if (objects.length === 3) {
      this._makePanel(-60, 0, objects[0][0], objects[0][1], 0.6);
      this._makePanel(0, 0, objects[1][0], objects[1][1], 0.6);
      this._makePanel(60, 0, objects[2][0], objects[2][1], 0.6);
    }
    if (objects.length === 4) {
      this._makePanel(-120, 0, objects[0][0], objects[0][1], 0.6);
      this._makePanel(-40, 0, objects[1][0], objects[1][1], 0.6);
      this._makePanel(40, 0, objects[2][0], objects[2][1], 0.6);
      this._makePanel(120, 0, objects[3][0], objects[3][1], 0.6);
    }
  }

  /**
   * Refreshes layout
   */
  _refreshPanelsHorizontalPositions() {
    const coll1 = G.l(-40);
    const coll2 = G.l(40);
    const row1 = G.l(-60);
    const row2 = G.l(30);

    if (this.panels.length === 1) {
      this.panels[0].x = 0;
      this.panels[0].y = -25;
      this.panels[0].scale.setTo(0.8);
    } else if (this.panels.length === 2) {
      this.panels[0].x = 0;
      this.panels[0].y = row1;
      this.panels[0].scale.setTo(0.6);
      this.panels[1].x = 0;
      this.panels[1].y = row2;
      this.panels[1].scale.setTo(0.6);
    } else if (this.panels.length === 3) {
      this.panels[0].x = coll1;
      this.panels[0].y = row1;
      this.panels[0].scale.setTo(0.6);
      this.panels[1].x = coll2;
      this.panels[1].y = row1;
      this.panels[1].scale.setTo(0.6);
      this.panels[2].x = coll1;
      this.panels[2].y = row2;
      this.panels[2].scale.setTo(0.6);
    } else if (this.panels.length === 4) {
      this.panels[0].x = coll1;
      this.panels[0].y = row1;
      this.panels[0].scale.setTo(0.6);
      this.panels[1].x = coll2;
      this.panels[1].y = row1;
      this.panels[1].scale.setTo(0.6);
      this.panels[2].x = coll1;
      this.panels[2].y = row2;
      this.panels[2].scale.setTo(0.6);
      this.panels[3].x = coll2;
      this.panels[3].y = row2;
      this.panels[3].scale.setTo(0.6);
    }
  }
}
